import { useState, useEffect, useRef, useMemo } from 'react';
import { Search, X, ArrowRight, Trash2, Heart } from 'lucide-react';
import type { Producto } from '@/types';
import productosData from '@/data/productos.json';

// Definir nuevos tipos para los resultados de búsqueda
interface TratamientoResult {
  type: 'tratamiento';
  nombre: string;
}

type SearchResult = Producto | TratamientoResult;

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectProduct: (producto: Producto) => void;
  onSelectTratamiento: (tratamiento: string) => void; // Nueva prop
}

export default function SearchModal({ 
  isOpen, 
  onClose, 
  onSelectProduct,
  onSelectTratamiento
}: SearchModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const productos = productosData as Producto[];

  const todosLosTratamientos = useMemo(() => {
    const tratamientosSet = new Set<string>();
    productos.forEach(p => {
      p.tratamientos.forEach(t => {
        if (t.trim()) tratamientosSet.add(t);
      });
    });
    return Array.from(tratamientosSet);
  }, [productos]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setQuery('');
      setResults([]);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (query.length >= 2) {
      const lowerCaseQuery = query.toLowerCase();
      
      // Filtrar productos
      const filteredProductos = productos.filter(
        (p) =>
          p.nombre.toLowerCase().includes(lowerCaseQuery) ||
          p.linea.toLowerCase().includes(lowerCaseQuery) ||
          p.tratamientos.some((t) =>
            t.toLowerCase().includes(lowerCaseQuery)
          )
      ).slice(0, 5);
      
      // Filtrar tratamientos
      const filteredTratamientos: TratamientoResult[] = todosLosTratamientos
        .filter(t => t.toLowerCase().includes(lowerCaseQuery))
        .map((t): TratamientoResult => ({ type: 'tratamiento', nombre: t }))
        .slice(0, 3);

      setResults([...filteredTratamientos, ...filteredProductos]);
    } else {
      setResults([]);
    }
  }, [query, productos, todosLosTratamientos]);

  const handleSelectProduct = (producto: Producto) => {
    onSelectProduct(producto);
    onClose();
  };
  
  const handleSelectTratamiento = (tratamiento: string) => {
    onSelectTratamiento(tratamiento);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center pt-24 md:pt-32">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      <div className="relative w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden animate-slide-up">
        <div className="flex items-center gap-4 p-4 border-b border-gray-100">
          <Search className="w-6 h-6 text-[#e91e8c]" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar productos, tratamientos..."
            className="flex-1 text-lg outline-none text-gray-800 placeholder:text-gray-400"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            >
              <Trash2 className="w-5 h-5 text-[#e91e8c]" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-[#e91e8c]" />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {query.length >= 2 ? (
            results.length > 0 ? (
              <div className="p-2">
                {results.map((item) => {
                  // Type guard para diferenciar resultados
                  if ('type' in item && item.type === 'tratamiento') {
                    return (
                      <button
                        key={item.nombre}
                        onClick={() => handleSelectTratamiento(item.nombre)}
                        className="w-full flex items-center gap-4 p-3 rounded-xl hover:bg-[#f8f9f5] transition-all duration-200 group"
                      >
                        <div className="w-14 h-14 rounded-lg bg-[#e91e8c]/10 flex items-center justify-center flex-shrink-0">
                          <Heart className="w-6 h-6 text-[#e91e8c]" />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-medium text-gray-800 group-hover:text-[#e91e8c] transition-colors">
                            {item.nombre}
                          </div>
                          <div className="text-sm text-gray-500">
                            Ir a la sección de Tratamiento
                          </div>
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-[#e91e8c] group-hover:translate-x-1 transition-all" />
                      </button>
                    );
                  } else {
                    const producto = item as Producto;
                    return (
                      <button
                        key={producto.codigo}
                        onClick={() => handleSelectProduct(producto)}
                        className="w-full flex items-center gap-4 p-3 rounded-xl hover:bg-[#f8f9f5] transition-all duration-200 group"
                      >
                        <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-[#f8f9f5] to-[#e8efe9] flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {producto.imagen && producto.imagen.length > 0 ? (
                            <img 
                              src={producto.imagen[0]} 
                              alt={producto.nombre} 
                              className="w-full h-full object-contain" 
                            />
                          ) : (
                            <span className="text-2xl">🌿</span>
                          )}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-medium text-gray-800 group-hover:text-[#4a7c59] transition-colors">
                            {producto.nombre}
                          </div>
                          <div className="text-sm text-gray-500">
                            {producto.linea}
                          </div>
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-[#4a7c59] group-hover:translate-x-1 transition-all" />
                      </button>
                    );
                  }
                })}
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="text-4xl mb-3">🔍</div>
                <p className="text-gray-500">
                  No se encontraron resultados para "{query}"
                </p>
              </div>
            )
          ) : (
            <div className="p-6">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
                Búsquedas populares
              </div>
              <div className="flex flex-wrap gap-2">
                {['Colágeno', 'Magnesio', 'Creatina', 'Dormir Mejor', 'Detox', 'Digestión', 'Antienvejecimiento', 'Ashwagandha', 'Dolor Articular'].map(
                  (term) => (
                    <button
                      key={term}
                      onClick={() => setQuery(term)}
                      className="px-4 py-2 bg-[#f8f9f5] rounded-full text-sm text-gray-600 hover:bg-[#e91e8c] hover:text-white transition-all duration-200"
                    >
                      {term}
                    </button>
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
